/** The chat's many names: a timeline draws down the card and each new name strikes out the one before. */

import { Fit, Scene, Zone, useStageExtra, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";
import { shortDate } from "../shared";

const BLOBS: BlobSpec[] = [{ colour: "fuchsia", x: 1020, y: 1780, r: 220, seed: 15.2, wob: 0.2 }];

/** The most names that fit down the card; older ones are counted, not shown. */
const SHOWN = 8;
const TOP = 420;
const STEP = 150;
/** Seconds between one name landing and the next. */
const BEAT = 0.42;

/** Whoever renamed the chat most, when that was more than once (two can share it). */
function topRenamers(names: Chat["names"]) {
  const counts = new Map<string, number>();
  for (const n of names) if (n.by) counts.set(n.by, (counts.get(n.by) ?? 0) + 1);
  const times = Math.max(0, ...counts.values());
  const who = [...counts].filter(([, n]) => n === times).map(([name]) => name);
  return times > 1 && who.length <= 2 ? { who, times } : null;
}

export function ChatNames({ names }: { names: Chat["names"] }) {
  // A taller screen spaces the names out, so a full list still reaches as far down the card.
  const step = STEP + Math.round(useStageExtra() / (SHOWN - 1));
  const shown = names.slice(-SHOWN);
  const hidden = names.length - shown.length;
  const top = topRenamers(names);
  const last = shown.length - 1;

  const root = useTimeline((s) => {
    s.rise(s.q("[data-part=head]"), 0.35, { y: 30, stagger: 0.12 });
    if (hidden) s.rise("[data-part=earlier]", 0.5, { y: 0, sound: false });
    const rows = s.q(".nm");
    if (!rows.length) return;
    // The line draws down at the pace the names arrive.
    s.tl.fromTo(".tl-line", { scaleY: 0 }, { scaleY: 1, duration: last * BEAT + 0.36, ease: "none" }, 0.55);
    rows.forEach((row, i) => {
      const at = 0.6 + i * BEAT;
      s.tl.fromTo(row, { x: 120, autoAlpha: 0 }, { x: 0, autoAlpha: 1, duration: 0.42, ease: "back.out(1.6)" }, at);
      s.sound("pop", at, 1 + i * 0.06);
      if (i > 0) {
        // The new name crosses out the old one, which fades back a little.
        const prev = rows[i - 1];
        s.tl.fromTo(prev.querySelector(".strike"), { scaleX: 0 }, { scaleX: 1, duration: 0.25, ease: "power2.out" }, at + 0.1);
        s.sound("scratch", at + 0.1, 1 + i * 0.03);
        s.tl.to(prev, { opacity: 0.72, duration: 0.25, ease: "none" }, at + 0.1);
      }
    });
    // The current name gives a little pulse once it has landed.
    const end = 0.6 + last * BEAT + 0.36;
    s.tl.fromTo(
      rows[last].querySelector(".nn"),
      { scale: 1 },
      { scale: 1.08, transformOrigin: "0% 50%", duration: 0.25, yoyo: true, repeat: 1, ease: "power1.out" },
      end,
    );
    if (top) s.rise("[data-part=foot]", end + 0.2, { y: 24 });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        {/* The list grows down from the heading rather than moving, so it all keeps to the top. */}
        <Zone at="top">
          <div data-part="head" className="kick">
            The chat's many names
          </div>
          <div data-part="head" className="lead">
            {names.length === 1 ? "1 new name this year" : `${names.length} names in one year`}
          </div>
          {hidden ? (
            <div
              data-part="earlier"
              className="abs mono"
              style={{ left: 164, top: 362, fontSize: 26, fontWeight: 700, letterSpacing: "0.04em", color: "var(--wr-gold-ink)" }}
            >
              +{hidden} EARLIER {hidden === 1 ? "NAME" : "NAMES"}
            </div>
          ) : null}
          {/* The line runs from the first dot to just past the last. */}
          <div className="tl-line" style={{ height: Math.max(0, last) * step + 50 }} />
          {shown.map((rename, i) => {
            const struck = i < last;
            return (
              <div
                key={`${rename.at}-${i}`}
                data-testid="chat-name"
                className="nm"
                style={{ top: TOP + i * step, opacity: struck ? 0.72 : undefined }}
              >
                <i aria-hidden />
                <div className="mono">{shortDate(rename.at).toUpperCase()}</div>
                {/* Flex, so the box is exactly the name's height and Fit can shrink a long one. */}
                <Fit size={64} min={26} style={{ display: "flex" }}>
                  <span className="nn" style={{ fontSize: "inherit" }}>
                    {rename.name}
                    <span className="strike" style={struck ? { transform: "scaleX(1)" } : undefined} />
                  </span>
                </Fit>
                {rename.by ? <span className="by">by {rename.by}</span> : null}
              </div>
            );
          })}
          {top ? (
            <div data-part="foot" className="line" style={{ top: TOP + last * step + 190 }}>
              {top.who.join(" and ")} renamed it <b>{top.times}</b> times{top.who.length > 1 ? " each" : ""}.
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
